import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axiosInstance from '../../../services/axiosInstance';
import { toast } from 'react-toastify';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const SubscriptionDialog = ({
  open,
  onClose,
  clientSecret,
  membershipName,
  membershipPrice,
  intentType,
  nextChargeAt,
  isSchoolMembership = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('new');
  const isSetupIntent = intentType === 'setup_intent';
  const formattedNextCharge = nextChargeAt
    ? new Date(nextChargeAt).toLocaleString('es-ES', {
        dateStyle: 'long',
        timeStyle: 'short',
      })
    : null;

  useEffect(() => {
    if (!open) {
      return;
    }

    axiosInstance
      .get('/users/saved-cards')
      .then(response => {
        const cards = response.cards || [];
        setSavedCards(cards);
        if (cards.length > 0) {
          setSelectedCard(cards[0].id);
        } else {
          setSelectedCard('new');
        }
      })
      .catch(error => {
        console.error('Error obteniendo tarjetas guardadas:', error);
      });
  }, [open]);

  const handlePayment = async e => {
    e.preventDefault();

    if (!stripe || !elements) {
      alert('Stripe no está listo. Por favor, intenta nuevamente.');
      return;
    }

    if (!clientSecret) {
      alert('No se pudo iniciar el proceso de pago. Intenta nuevamente.');
      return;
    }

    setLoading(true);

    try {
      let confirmOptions;

      if (selectedCard === 'new' || savedCards.length === 0) {
        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
          alert(
            'No se pudo cargar el formulario de tarjeta. Intenta nuevamente.'
          );
          setLoading(false);
          return;
        }

        confirmOptions = {
          payment_method: {
            card: cardElement,
          },
        };
      } else {
        confirmOptions = {
          payment_method: selectedCard,
        };
      }

      if (isSetupIntent) {
        const { error, setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          confirmOptions
        );

        if (error) {
          console.error('Error guardando el método de pago:', error.message);
          alert('Error al guardar el método de pago. Inténtalo de nuevo.');
        } else if (setupIntent && setupIntent.status === 'succeeded') {
          toast.success('Método de pago guardado y suscripción activada.');
          onClose();
        }
      } else {
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          confirmOptions
        );

        if (error) {
          console.error('Error procesando el pago:', error.message);
          alert('Error al procesar el pago. Inténtalo de nuevo.');
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          toast.success(
            `Suscripción completada con éxito a ${membershipName}.`
          );
          onClose();
        }
      }
    } catch (err) {
      console.error('Error al procesar el pago:', err.message);
      alert('Ocurrió un error. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 'bold',
          textAlign: 'center',
          py: 2,
        }}
      >
        Completar Suscripción
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        <Typography variant="body1">
          Estás a punto de suscribirte a <strong>{membershipName}</strong>.
        </Typography>
        {isSchoolMembership && (
          <Typography
            variant="body2"
            sx={{ color: 'error.main', fontWeight: 'bold', mt: 1 }}
          >
            Por favor rogamos no se realice ningún pago si no tiene asignado un
            grupo. Contactar con Alberto el monitor.
          </Typography>
        )}
        <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
          Total a Pagar: €{membershipPrice}
        </Typography>

        {isSetupIntent && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {formattedNextCharge
              ? `No se realizará ningún cobro hoy. El primer cobro se realizará el ${formattedNextCharge}.`
              : 'No se realizará ningún cobro hoy. Guardaremos tu método de pago para los próximos cobros.'}
          </Alert>
        )}

        {savedCards.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 3 }}>
              Selecciona un método de pago:
            </Typography>
            <RadioGroup
              value={selectedCard}
              onChange={event => setSelectedCard(event.target.value)}
            >
              {savedCards.map(card => (
                <FormControlLabel
                  key={card.id}
                  value={card.id}
                  control={<Radio />}
                  label={`${card.brand.toUpperCase()} **** ${card.last4} (Exp: ${card.expMonth}/${card.expYear})`}
                />
              ))}
              <FormControlLabel
                value="new"
                control={<Radio />}
                label="Usar nueva tarjeta"
              />
            </RadioGroup>
          </>
        )}

        {(selectedCard === 'new' || savedCards.length === 0) && (
          <Box
            sx={{
              marginTop: '20px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
            }}
          >
            <CardElement options={{ hidePostalCode: true }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handlePayment}
          variant="contained"
          color="primary"
          disabled={loading || !stripe || !elements || !clientSecret}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : isSetupIntent ? (
            'Guardar método de pago'
          ) : (
            'Suscribirse'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Wrapper to initialize Stripe Elements
const SubscriptionPaymentDialog = props => (
  <Elements stripe={stripePromise}>
    <SubscriptionDialog {...props} />
  </Elements>
);

export default SubscriptionPaymentDialog;
